import {
  Button,
  type ControlledSearch,
  DataTable,
  type LocalDataColumn,
  TOUCH,
} from "@nymbl/nos2-rules";
import type * as React from "react";
import { useMemo } from "react";
import type { OpenPerson, PanelTab } from "@/components/team/panel-state";
import {
  byName,
  DEFAULT_DETAILS_SORT,
  DETAIL_COLUMNS,
  DETAILS_TABLE_COLUMNS,
  type Drill,
  formatDetail,
} from "@/lib/team/details";
import type { PersonMetrics, TeamTotals } from "@/lib/team/metrics";
import type { IsoDate } from "@/lib/time/dates";

const DRILL_TAB: Record<Drill, PanelTab> = {
  guidance: "guidance",
  billable: "entries",
  entries: "entries",
};
const DRILL_TITLE: Record<Drill, string> = {
  guidance: "Open guidance",
  billable: "Open billable entries",
  entries: "Open time entries",
};

interface DetailsTableProps {
  people: PersonMetrics[];
  totals: TeamTotals;
  month: IsoDate;
  onOpen: OpenPerson;
  controls: React.ReactNode;
  /** One search across Summary and Details. */
  search: ControlledSearch;
}

/** Every number by person. The Team line is the whole team's, whatever the search or filters leave. */
export function DetailsTable({
  people,
  totals,
  month,
  onOpen,
  controls,
  search,
}: DetailsTableProps) {
  const rows = useMemo(() => [...people].sort(byName), [people]);
  const columns = useMemo<LocalDataColumn<PersonMetrics>[]>(
    () =>
      DETAILS_TABLE_COLUMNS.map((column) => {
        const detail = DETAIL_COLUMNS.find((c) => c.key === column.key);
        if (!detail) {
          return {
            ...column,
            className: "min-w-44",
            render: (p: PersonMetrics) => (
              <Button
                variant="link"
                className={`h-auto px-0 font-semibold ${TOUCH}`}
                onClick={() => onOpen(p.personId, "summary")}
              >
                {p.personName}
              </Button>
            ),
          };
        }
        return {
          ...column,
          total: () => formatDetail(detail, totals),
          render: (p: PersonMetrics) => {
            const text = formatDetail(detail, p);
            const drill = detail.drill;
            if (drill && text !== "—") {
              return (
                <Button
                  variant="link"
                  className={`h-auto px-0 tabular-nums ${TOUCH}`}
                  title={DRILL_TITLE[drill]}
                  aria-label={`${DRILL_TITLE[drill]}, ${p.personName}: ${text}`}
                  onClick={() =>
                    onOpen(p.personId, DRILL_TAB[drill], drill === "billable" ? "billable" : "all")
                  }
                >
                  {text}
                </Button>
              );
            }
            const red = detail.key === "billablePct" && p.belowTarget;
            return red ? <span className="font-semibold text-danger">{text}</span> : text;
          },
        };
      }),
    [onOpen, totals],
  );

  return (
    <DataTable
      searchState={search}
      label="Team details"
      exportTotals
      title="Team · to date"
      search={{ placeholder: "Search people…" }}
      exportName={`my-team-${month.slice(0, 7)}`}
      controls={controls}
      columns={columns}
      rows={rows}
      rowKey={(p) => p.personId}
      defaultSort={DEFAULT_DETAILS_SORT}
      stickyFirstColumn
    />
  );
}
